import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  CircularProgress,
  Divider,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useSnackbar } from '../contexts/SnackbarContext.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import {
  performanceEventsApi,
  PerformanceEvent,
  RecurrenceFrequency,
} from '../services/performanceEventsService.ts';

const RECURRENCE_FREQUENCIES: RecurrenceFrequency[] = ['Daily', 'Weekly', 'Monthly', 'Yearly'];
const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// Computed from the date input using local calendar components (avoids UTC-shift off-by-one)
const computeWeekDay = (dateStr: string): string => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-').map(Number);
  if (!year || !month || !day) return '';
  const date = new Date(year, month - 1, day);
  return DAYS_OF_WEEK[date.getDay()];
};

const PerformanceEventFormPage: React.FC = () => {
  const { showSuccess, showError } = useSnackbar();
  const { sessionToken } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const performanceEventId = searchParams.get('id');

  const [formData, setFormData] = useState({
    performanceName: '',
    performerName: '',
    eventDate: '',
    isRecurring: false,
    recurrenceFrequency: '' as RecurrenceFrequency | '',
    recurrenceDayOfWeek: '',
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'pending' | 'saving' | 'saved' | 'error'>('idle');
  const [autoSaveError, setAutoSaveError] = useState('');
  const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const fetchPerformanceEvent = async () => {
      if (!performanceEventId || !sessionToken) return;

      setLoading(true);
      try {
        const data = await performanceEventsApi.getPerformanceEvent(parseInt(performanceEventId), sessionToken);
        setFormData({
          performanceName: data.performanceName || '',
          performerName: data.performerName || '',
          eventDate: data.eventDate ? data.eventDate.substring(0, 10) : '',
          isRecurring: data.isRecurring,
          recurrenceFrequency: data.recurrenceFrequency ?? '',
          recurrenceDayOfWeek: data.recurrenceDayOfWeek ?? '',
        });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch performance event';
        showError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchPerformanceEvent();
  }, [performanceEventId, sessionToken]);

  // Prefill form when navigating from list "Copy" (create new from existing record)
  useEffect(() => {
    const copyFrom = location.state?.copyFrom as PerformanceEvent | undefined;
    if (performanceEventId || !copyFrom) return;

    setFormData({
      performanceName: copyFrom.performanceName || '',
      performerName: copyFrom.performerName || '',
      eventDate: copyFrom.eventDate ? copyFrom.eventDate.substring(0, 10) : '',
      isRecurring: copyFrom.isRecurring,
      recurrenceFrequency: copyFrom.recurrenceFrequency ?? '',
      recurrenceDayOfWeek: copyFrom.recurrenceDayOfWeek ?? '',
    });
    navigate(location.pathname, { replace: true, state: {} });
  }, [performanceEventId, location.state?.copyFrom, location.pathname, navigate]);

  const validateFormData = (data: typeof formData): string | null => {
    if (!data.performanceName.trim()) return 'Performance Name is required';
    if (!data.performerName.trim()) return 'Performer Name is required';
    if (!data.eventDate) return 'Date is required';
    if (data.isRecurring && !data.recurrenceFrequency) return 'Please select a recurrence frequency';
    return null;
  };

  const buildApiData = (data: typeof formData) => ({
    performanceName: data.performanceName,
    performerName: data.performerName,
    eventDate: data.eventDate,
    isRecurring: data.isRecurring,
    recurrenceFrequency: data.isRecurring ? (data.recurrenceFrequency || null) : null,
    recurrenceDayOfWeek: data.isRecurring ? (data.recurrenceDayOfWeek || null) : null,
  });

  // Auto-saves ~900ms after the last change to any field, while editing an existing
  // performance event. Creating a new one still requires the explicit Save button.
  const performAutoSave = useCallback(
    async (id: number, snapshot: typeof formData) => {
      if (!sessionToken) return;
      const validationError = validateFormData(snapshot);
      if (validationError) {
        setAutoSaveStatus('error');
        setAutoSaveError(validationError);
        return;
      }
      setAutoSaveStatus('saving');
      try {
        await performanceEventsApi.updatePerformanceEvent(id, buildApiData(snapshot), sessionToken);
        setAutoSaveStatus('saved');
      } catch (err) {
        setAutoSaveStatus('error');
        setAutoSaveError(err instanceof Error ? err.message : 'Failed to save changes');
      }
    },
    [sessionToken]
  );

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (performanceEventId) {
        const id = parseInt(performanceEventId, 10);
        if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
        setAutoSaveStatus('pending');
        autoSaveTimeoutRef.current = setTimeout(() => {
          autoSaveTimeoutRef.current = null;
          performAutoSave(id, next);
        }, 900);
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!sessionToken) return;

    const validationError = validateFormData(formData);
    if (validationError) {
      showError(validationError);
      return;
    }

    setSaving(true);
    try {
      const apiData = buildApiData(formData);

      if (performanceEventId) {
        await performanceEventsApi.updatePerformanceEvent(parseInt(performanceEventId), apiData, sessionToken);
        showSuccess('Performance event updated successfully!');
      } else {
        const result = await performanceEventsApi.createPerformanceEvent(apiData, sessionToken);
        showSuccess(
          result.occurrencesCreated > 1
            ? `Created ${result.occurrencesCreated} occurrences (${formData.recurrenceFrequency}, three months ahead)!`
            : 'Performance event created successfully!'
        );
        setSearchParams({ id: result.event.id.toString() });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save performance event';
      showError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const weekDay = computeWeekDay(formData.eventDate);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Typography variant="h6" sx={{ color: '#7f0000', fontWeight: 700, flex: '0 0 auto', whiteSpace: 'nowrap' }}>
          Date: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </Typography>
        <Typography variant="h4" component="h1" sx={{ flex: 1, textAlign: 'center', fontWeight: 700, textTransform: 'uppercase' }}>
          Performance Events
        </Typography>
      </Box>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, mb: 3 }}>
          {performanceEventId && (
            <Typography
              variant="body2"
              color={autoSaveStatus === 'error' ? 'error' : 'text.secondary'}
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              {autoSaveStatus === 'saving' && (<><CircularProgress size={14} />Saving…</>)}
              {autoSaveStatus === 'pending' && 'Unsaved changes…'}
              {autoSaveStatus === 'saved' && 'All changes saved'}
              {autoSaveStatus === 'error' && (autoSaveError || 'Failed to save changes')}
            </Typography>
          )}
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            size="large"
          >
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Performance Name"
              value={formData.performanceName}
              onChange={(e) => handleInputChange('performanceName', e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Performer Name"
              value={formData.performerName}
              onChange={(e) => handleInputChange('performerName', e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Date"
              type="date"
              value={formData.eventDate}
              onChange={(e) => handleInputChange('eventDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Week Day"
              value={weekDay}
              InputProps={{ readOnly: true }}
              helperText="Automatically calculated from the date"
            />
          </Grid>
        </Grid>

        <Paper sx={{ p: 2, mb: 1, backgroundColor: 'grey.50' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Occurrence
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={formData.isRecurring}
                onChange={(e) => handleInputChange('isRecurring', e.target.checked)}
                disabled={!!performanceEventId}
              />
            }
            label="Is it recurring?"
            sx={{ mb: 1 }}
          />
          {!performanceEventId && formData.isRecurring && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Saving will create one performance event for every occurrence (based on the frequency below),
              starting on the date above and running for three months. Each occurrence appears separately in the
              list and can be edited, printed, copied, or deleted on its own, or the whole series can be deleted
              at once from the list.
            </Typography>
          )}
          {!!performanceEventId && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This is a single occurrence. Recurrence settings here are informational only &mdash; editing them
              affects just this date, not the rest of the series.
            </Typography>
          )}
          {formData.isRecurring && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel id="recurrence-frequency-label">Frequency</InputLabel>
                  <Select
                    labelId="recurrence-frequency-label"
                    label="Frequency"
                    value={formData.recurrenceFrequency}
                    onChange={(e) => handleInputChange('recurrenceFrequency', e.target.value)}
                  >
                    {RECURRENCE_FREQUENCIES.map((freq) => (
                      <MenuItem key={freq} value={freq}>
                        {freq}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="recurrence-day-of-week-label">Day of the Week</InputLabel>
                  <Select
                    labelId="recurrence-day-of-week-label"
                    label="Day of the Week"
                    value={formData.recurrenceDayOfWeek}
                    onChange={(e) => handleInputChange('recurrenceDayOfWeek', e.target.value)}
                  >
                    <MenuItem value="">
                      <em>&mdash;</em>
                    </MenuItem>
                    {DAYS_OF_WEEK.map((day) => (
                      <MenuItem key={day} value={day}>
                        {day}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          )}
        </Paper>
      </Paper>
    </Box>
  );
};

export default PerformanceEventFormPage;
